import { useState, useEffect } from 'react';
import { Plus, Trash2, Edit2, Save, X, Eye, EyeOff, GripVertical, Loader2, Badge } from 'lucide-react';

import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card } from '../ui/card';
import { adminFetch, getAuthHeaders } from '../../services/adminApi';

import { PageTitle } from './shared';

const TrustIndicatorsEditor = () => {
  const [indicators, setIndicators] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [message, setMessage] = useState('');
  const [newIndicator, setNewIndicator] = useState({ text: '', order: 0, visible: true });
  const [showAddForm, setShowAddForm] = useState(false);

  useEffect(() => {
    fetchIndicators();
  }, []);

  const fetchIndicators = async () => {
    setLoading(true);
    try {
      const response = await adminFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/trust-indicators`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setIndicators(data);
      }
    } catch (error) {
      console.error('Failed to load trust indicators:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newIndicator.text.trim()) {
      setMessage('Please enter indicator text');
      return;
    }

    try {
      const response = await adminFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/trust-indicators`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          ...newIndicator,
          order: indicators.length + 1
        })
      });

      if (response.ok) {
        setMessage('Trust indicator added!');
        setNewIndicator({ text: '', order: 0, visible: true });
        setShowAddForm(false);
        fetchIndicators();
        setTimeout(() => setMessage(''), 3000);
      } else {
        throw new Error('Failed to create');
      }
    } catch (error) {
      setMessage('Failed to create indicator');
    }
  };

  const handleUpdate = async (indicatorId) => {
    try {
      const response = await adminFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/trust-indicators/${indicatorId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(editData)
      });

      if (response.ok) {
        setMessage('Updated successfully!');
        setEditingId(null);
        setEditData({});
        fetchIndicators();
        setTimeout(() => setMessage(''), 3000);
      } else {
        throw new Error('Failed to update');
      }
    } catch (error) {
      setMessage('Failed to update indicator');
    }
  };

  const handleDelete = async (indicatorId) => {
    if (!window.confirm('Are you sure you want to delete this indicator?')) return;

    try {
      const response = await adminFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/trust-indicators/${indicatorId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        setMessage('Deleted successfully!');
        setIndicators(indicators.filter(i => i.id !== indicatorId));
        setTimeout(() => setMessage(''), 3000);
      } else {
        throw new Error('Failed to delete');
      }
    } catch (error) {
      setMessage('Failed to delete indicator');
    }
  };

  const toggleVisibility = async (indicator) => {
    const newVisibility = !indicator.visible;
    try {
      const response = await adminFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/trust-indicators/${indicator.id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ visible: newVisibility })
      });

      if (response.ok) {
        setIndicators(indicators.map(i =>
          i.id === indicator.id ? { ...i, visible: newVisibility } : i
        ));
      }
    } catch (error) {
      console.error('Failed to toggle visibility');
    }
  };

  const startEdit = (indicator) => {
    setEditingId(indicator.id);
    setEditData({
      text: indicator.text,
      order: indicator.order,
      visible: indicator.visible
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center p-12">
        <Loader2 className="h-8 w-8 animate-spin text-red-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageTitle
        title="Trust Badges"
        description="Manage the badges displayed above the hero title"
        icon={<Badge className="h-6 w-6" />}
        actions={
          <Button
            onClick={() => setShowAddForm(!showAddForm)}
            variant="adminPrimary"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Badge
          </Button>
        }
      />

      {/* Add Form */}
      {showAddForm && (
        <div className="admin-card admin-corner-accent border-2 border-stone-200 rounded-lg p-6 bg-gradient-to-r from-stone-50 to-amber-50/30">
          <div className="space-y-3">
            <div>
              <Label className="text-gray-800 dark:text-gray-200 font-medium">Badge Text</Label>
              <Input
                value={newIndicator.text}
                onChange={(e) => setNewIndicator({ ...newIndicator, text: e.target.value })}
                placeholder="e.g., As seen on News 8, 9 & 10"
                className="border-stone-300 focus:border-amber-500 focus:ring-amber-500/20 bg-background"
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={handleCreate} variant="adminPrimary">
                <Save className="h-4 w-4 mr-2" />
                Save
              </Button>
              <Button onClick={() => setShowAddForm(false)} variant="adminOutline">
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Message */}
      {message && (
        <div className={`p-4 rounded-lg ${
          message.includes('!') && !message.includes('Failed')
            ? 'bg-green-50 text-green-700 border border-green-200'
            : 'bg-red-50 text-red-700 border border-red-200'
        }`}>
          {message}
        </div>
      )}

      {/* Indicators List */}
      <div className="space-y-3">
        {indicators.length === 0 ? (
          <div className="text-center py-12 bg-stone-50 rounded-lg border border-stone-200">
            <p className="text-stone-600 dark:text-stone-400">No trust badges yet</p>
            <p className="text-sm text-stone-500 dark:text-stone-400">Click "Add Badge" to create one</p>
          </div>
        ) : (
          indicators.map((indicator) => (
            <Card key={indicator.id} className="admin-card border-stone-200 p-4">
              {editingId === indicator.id ? (
                // Edit Mode
                <div className="space-y-3">
                  <div>
                    <Label className="text-gray-800 dark:text-gray-200 font-medium">Badge Text</Label>
                    <Input
                      value={editData.text}
                      onChange={(e) => setEditData({ ...editData, text: e.target.value })}
                      className="border-stone-300 focus:border-amber-500 focus:ring-amber-500/20 bg-background"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-800 dark:text-gray-200 font-medium">Order</Label>
                    <Input
                      type="number"
                      value={editData.order}
                      onChange={(e) => setEditData({ ...editData, order: parseInt(e.target.value) })}
                      className="w-24 border-stone-300 focus:border-amber-500 focus:ring-amber-500/20 bg-background"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={() => handleUpdate(indicator.id)} variant="adminPrimary" size="sm">
                      <Save className="h-3 w-3 mr-1" />
                      Save
                    </Button>
                    <Button onClick={() => { setEditingId(null); setEditData({}); }} variant="adminOutline" size="sm">
                      <X className="h-3 w-3 mr-1" />
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                // View Mode
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <GripVertical className="h-4 w-4 text-stone-400" />
                    <span className="text-sm text-stone-500 dark:text-stone-400">#{indicator.order}</span>
                    <span className={`font-medium ${indicator.visible ? 'text-gray-900 dark:text-gray-100' : 'text-stone-400'}`}>
                      {indicator.text}
                    </span>
                    {!indicator.visible && (
                      <span className="text-xs bg-stone-200 text-stone-600 dark:text-stone-400 px-2 py-0.5 rounded">Hidden</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={() => toggleVisibility(indicator)}
                      variant="ghost"
                      size="sm"
                      className="text-stone-600 dark:text-stone-400 hover:text-amber-600"
                    >
                      {indicator.visible ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                    </Button>
                    <Button
                      onClick={() => startEdit(indicator)}
                      variant="ghost"
                      size="sm"
                      className="text-stone-600 dark:text-stone-400 hover:text-amber-600"
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button
                      onClick={() => handleDelete(indicator.id)}
                      variant="ghost"
                      size="sm"
                      className="text-stone-600 dark:text-stone-400 hover:text-red-600"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default TrustIndicatorsEditor;
